import type { Capability } from '@gridfit/core/principals'
import type { MeResponse } from '@/auth/session'

/**
 * First match wins (design §4.3). Ordered by what the holder of that
 * capability actually spends their day in, not alphabetically: someone with
 * `commercial` is in the pipeline, and someone with only `ops` is on a site.
 *
 * FIXED, review round 1 (Important 2): this table originally pointed
 * `costs-entry` at `/money` and `client-portal` at `/portal`, while
 * `nav.ts`'s `NAV_ITEMS` gated `/money` on `commercial` only and had no
 * `/portal` entry — and `routes.tsx` had no `/portal` ROUTE at all.
 * Measured: a `costs-entry`-only principal landed on Money with an empty
 * nav; a `client-portal`-only principal crashed straight into react-router's
 * own development error boundary ("Unexpected Application Error!"), since
 * nothing matched `/portal` at all. `costs-entry` now targets `/costs` — a
 * new area of its own, gated on `costs-entry` in `nav.ts`, kept SEPARATE
 * from Money rather than widening Money's `commercial` gate (see `nav.ts`'s
 * own comment on why that specific fix was closed off). `client-portal`
 * still targets `/portal`, which now exists as a real route with a matching
 * nav item.
 *
 * STILL NOT enforced structurally, review round 2's Important 2: round 1's
 * fix repaired these two rows but left `PRIORITY` and `nav.ts`'s
 * `NAV_ITEMS` as two independent lists with nothing keeping a FUTURE row
 * consistent between them — round 1's own test only re-validated the five
 * rows it already knew about, hand-mirrored in the test file, so it could
 * catch a route or nav item being REMOVED but not a new row shipping the
 * same way `costs-entry`/`client-portal` originally did. Measured directly:
 * the re-reviewer added `['config-admin', '/nonexistent-route']` and
 * `['config-admin', '/admin']` to this table and every one of round 1's 13
 * shell tests stayed green. `landingTargets()` below is the fix —
 * `test/shell.test.tsx` now iterates THIS table directly rather than a
 * hand-mirrored copy, so a row added here is checked automatically, with no
 * second list to remember to update.
 */
const PRIORITY: ReadonlyArray<readonly [Capability, string]> = [
  ['commercial', '/pipeline'],
  ['ops', '/site-ops'],
  ['costs-entry', '/costs'],
  ['user-admin', '/admin'],
  ['client-portal', '/portal'],
]

/**
 * A read-only view over `PRIORITY`, exported ONLY so `test/shell.test.tsx`
 * can iterate the REAL table (review round 2's Important 2 — see the
 * comment above `PRIORITY` for what a hand-mirrored copy missed). Not a
 * second public landing rule: `landingFor` stays the one interface this
 * task's brief names as "produced", and the priority ORDER this exposes is
 * not meant as something a caller other than that one test should depend
 * on — "first match wins" is `landingFor`'s own contract, not a promise
 * about this accessor's shape.
 */
export function landingTargets(): ReadonlyArray<readonly [Capability, string]> {
  return PRIORITY
}

export const NO_AREAS = '/no-areas'

export function landingFor(me: MeResponse | undefined): string {
  if (!me) return '/login'
  for (const [capability, path] of PRIORITY) {
    if (me.capabilities.includes(capability)) return path
  }
  return NO_AREAS
}
